import React, { useMemo } from 'react'

import Tabs from '@/components/Tabs/Tabs'

import { InputSelect } from '../styled'
import { FormContext, type FormInputType } from '../types'

const FormInputMultiSelectTab: React.FC<{
  input: FormInputType & { type: 'multi-select-tab' }
}> = ({ input }) => {
  type LabelType = (typeof input)['options'][number]['label']

  const { name: inputName, initialValue, options, onChange: inputOnChange } = input

  const { submitting, onChange } = React.useContext(FormContext)

  const initialTabs = useMemo(
    () =>
      options
        .filter(option => initialValue != null && initialValue.includes(option.value))
        .map(t => t.label),
    [options, initialValue]
  )

  const [currentTabs, setCurrentTabs] = React.useState<LabelType[]>(initialTabs)
  const tabOptions = useMemo(() => options.map(option => option.label), [options])

  const handleChange = React.useCallback(
    (label: LabelType) => {
      if (submitting) {
        // No op when form is locked
        return
      }

      const newCurrentTabs = currentTabs.includes(label)
        ? currentTabs.filter(t => t !== label)
        : [...currentTabs, label]

      setCurrentTabs(newCurrentTabs)

      const newSelectedOptions = options
        .filter(option => newCurrentTabs.includes(option.label))
        .map(option => option.value)

      onChange(inputName, newSelectedOptions)
      inputOnChange?.(newSelectedOptions)
    },
    [submitting, onChange, inputName, currentTabs, options, inputOnChange]
  )

  return (
    <InputSelect>
      <Tabs
        variant="rounded"
        tabs={tabOptions}
        currentTab={currentTabs}
        onTabChange={handleChange}
        buttonProps={{
          minWidth: 0,
          flex: 1,
          p: { base: '2px', sm: 'auto' },
        }}
      />
    </InputSelect>
  )
}

export default FormInputMultiSelectTab
